import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { useInfiniteScroll } from "@/modules/core/hooks/useInfiniteScroll"
import { word } from "@/modules/core/utils/i18n"
import { SocialCommentItem } from "@/modules/social/components/socialCommentItem"
import { useSocialComments } from "@/modules/social/hooks/useSocialComments"
import { MessageCircleOff } from "lucide-react"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export type SocialCommentListProps = {
  socialPostId: string | null
  onReply: (commentId: string, username?: string) => void
  autoExpandCommentId?: string | null
}

export function SocialCommentList({ socialPostId, onReply, autoExpandCommentId }: SocialCommentListProps) {
  if (!socialPostId) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <MessageCircleOff className="size-10 text-textOnSurface-disabled" />
        <Text fontStyle="BM" className="text-center text-textOnSurface-subtle">
          {word("social.view.noSocialPost")}
        </Text>
      </div>
    )
  }

  return (
    <ErrorBoundary
      fallback={
        <Text fontStyle="BM" className="text-feedback-error-text py-4 text-center">
          {word("social.comment.list.error")}
        </Text>
      }
    >
      <Suspense fallback={<SocialCommentListSkeleton />}>
        <SocialCommentListInner
          socialPostId={socialPostId}
          onReply={onReply}
          autoExpandCommentId={autoExpandCommentId}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function SocialCommentListInner({
  socialPostId,
  onReply,
  autoExpandCommentId,
}: {
  socialPostId: string
  onReply: (commentId: string, username?: string) => void
  autoExpandCommentId?: string | null
}) {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useSocialComments(socialPostId)
  const { InfiniteScrollTrigger } = useInfiniteScroll({ hasNextPage, fetchNextPage, isFetchingNextPage })

  const allComments = data.pages.flatMap((page) => page.content)

  if (allComments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <MessageCircleOff className="size-10 text-textOnSurface-disabled" />
        <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textOnSurface-subtle">
          {word("social.comment.list.empty")}
        </Text>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {allComments.map((comment) => (
        <SocialCommentItem
          key={comment.id}
          socialPostId={socialPostId}
          comment={comment}
          onReply={onReply}
          defaultShowReplies={autoExpandCommentId === comment.id}
        />
      ))}
      <InfiniteScrollTrigger />
    </div>
  )
}

function SocialCommentListSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      ))}
    </div>
  )
}
